import React from 'react';
import { Flex, Box, TextV2 } from '@withjoy/joykit';
import { Close, Restart } from '@withjoy/joykit/icons';
import { useTranslation } from '@shared/core';
import { PRICE_RANGE } from '../../../../constants';
import { Filters } from '@apps/accommodations/AccommodationsConsole.types';
import { DEFAULT_FILTERS } from '@apps/accommodations/hooks/useAccommodationsConsoleSearchParamsState';

interface ResetFiltersHeaderProps {
  filters: Filters;
  updateFilters: (filters: Filters) => void;
}

interface FilterTagProps {
  label: string;
  onRemove: () => void;
}

const FilterTag = ({ label, onRemove }: FilterTagProps) => (
  <Box
    display="flex"
    alignItems="center"
    gap={1}
    paddingX={5}
    paddingY={3}
    backgroundColor="mono2"
    borderRadius="full"
  >
    <TextV2 typographyVariant="label2">{label}</TextV2>
    <Box
      as="button"
      onClick={onRemove}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onRemove();
        }
      }}
      display="flex"
      alignItems="center"
      aria-label={`Remove ${label} filter`}
      role="button"
      tabIndex={0}
    >
      <Close size={14} />
    </Box>
  </Box>
);

export const ResetFiltersHeader = ({ filters, updateFilters }: ResetFiltersHeaderProps) => {
  const { t } = useTranslation('accommodationsConsole');
  const resetFiltersHeaderTrans = t('listingsPanel', 'resetFiltersHeader');

  const hasPriceFilter = filters?.priceRange && (filters.priceRange[0] !== PRICE_RANGE.MIN || filters.priceRange[1] !== PRICE_RANGE.MAX);
  const hasFacilitiesFilter = filters?.facilities && filters.facilities.length > 0;

  const hasStarRating = filters?.starRating?.length > 0;
  const hasGuestRating = filters?.userRating?.length > 0;
  const hasAnyFilter = hasPriceFilter || hasFacilitiesFilter || hasStarRating || hasGuestRating;

  if (!hasAnyFilter) {
    return null;
  }

  return (
    <Flex
      gap={2}
      flexWrap="wrap"
      paddingX={4}
      paddingY={2}
      justifyContent="space-between"
    >
      <Flex
        gap={2}
        flexWrap="wrap"
      >
        {hasPriceFilter && filters?.priceRange && (
          <FilterTag
            label={resetFiltersHeaderTrans.price({ min: `$${filters.priceRange[0]}`, max: `$${filters.priceRange[1]}` })}
            onRemove={() => updateFilters({ ...filters, priceRange: [PRICE_RANGE.MIN, PRICE_RANGE.MAX] })}
          />
        )}
        {hasStarRating && filters?.starRating && (
          <FilterTag
            label={resetFiltersHeaderTrans.starRating({ starRating: filters.starRating.sort((a, b) => a - b).join(', ') })}
            onRemove={() => updateFilters({ ...filters, starRating: [] })}
          />
        )}
        {hasGuestRating && filters?.userRating && (
          <FilterTag
            label={resetFiltersHeaderTrans.guestRating({ guestRating: filters.userRating.map(r => `${r}+`).join(', ') })}
            onRemove={() => updateFilters({ ...filters, userRating: [] })}
          />
        )}
        {filters?.facilities?.map(facility => (
          <FilterTag
            key={facility}
            label={facility}
            onRemove={() =>
              updateFilters({
                ...filters,
                facilities: filters.facilities.filter(f => f !== facility),
              })
            }
          />
        ))}
      </Flex>
      {hasAnyFilter && (
        <Box
          as="button"
          display="flex"
          alignItems="center"
          onClick={() => updateFilters(DEFAULT_FILTERS)}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              updateFilters(DEFAULT_FILTERS);
            }
          }}
          gap={1}
          paddingX={5}
          paddingY={3}
          backgroundColor="mono2"
          borderRadius="full"
          cursor={'pointer'}
          aria-label="Reset all filters"
          role="button"
          tabIndex={0}
        >
          <Box
            display="flex"
            alignItems="center"
          >
            <Restart size={16} />
          </Box>
          <TextV2 typographyVariant="label2">{resetFiltersHeaderTrans.resetAll()}</TextV2>
        </Box>
      )}
    </Flex>
  );
};
